import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { Alert, Button, Icons, Spinner, cx } from '../../components/ui.tsx';
import { useToast } from '../../components/Toast.tsx';
import { formatDate, formatRelative } from '../../lib/display.ts';

/**
 * A link anybody can open.
 *
 * The system shares by account: somebody is given a login and a level. That is
 * right for the office and no use for the commonest thing of all — sending one
 * invoice to one customer who will never have an account here. So this mints a
 * link instead, and the shape of the dialog says what a link is and is not.
 *
 * A file or a letter shared this way is always view-only: there is nothing to
 * write into, and anybody who should be able to *change* one gets an account,
 * because an account can be suspended and a forwarded link cannot be
 * un-forwarded. A folder is a place rather than a record, so it offers the
 * three levels in LEVELS.
 *
 * The link is off until somebody turns it on, and turning it off again is one
 * click — that is the control that matters most here, and it is the one that
 * should be easiest to find.
 */

type Kind = 'FILE' | 'DOCUMENT' | 'SPREADSHEET' | 'FOLDER';

/** What a folder link lets the holder do. Only ever VIEW on anything else. */
type Access = 'VIEW' | 'EDIT' | 'DELETE';

/**
 * What a folder link lets the holder do.
 *
 * Only a folder offers these. A file shared by link is a copy of a record —
 * there is nothing to write into — and the server enforces that whatever this
 * page sends.
 *
 * Viewing first and selected, because it is the one that cannot go wrong. The
 * wording says what each one means for a stranger rather than naming a role:
 * "Editor" tells somebody what they are called, not what they can do to your
 * paperwork.
 */
const LEVELS = [
  {
    id: 'VIEW' as const,
    label: 'Can view',
    detail: 'Open and read what is in the folder, and take a copy.',
  },
  {
    id: 'EDIT' as const,
    label: 'Can add and rename',
    detail: 'Also upload files, make folders, and rename what is there.',
  },
  {
    id: 'DELETE' as const,
    label: 'Can remove things',
    detail: 'Also move things to your trash, where you can put them back.',
  },
];

/**
 * How long a link lasts.
 *
 * Thirty days first and selected, because that is what an invoice sent to a
 * customer needs and because a link with no end is the kind that turns up two
 * years later in a forwarded email, still working. "Never" is there for the
 * cases that genuinely want it — a price list on a website — and says what it
 * is rather than hiding.
 */
const DURATIONS = [
  { days: 30, label: '30 days' },
  { days: 7, label: '7 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: 'A year' },
  { days: 0, label: 'Never' },
] as const;

interface ShareLink {
  id: string;
  token: string;
  allowDownload: boolean;
  access: Access;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  lastViewedAt: string | null;
  views: number;
  createdBy: { id: string; name: string } | null;
}

export function ShareLinkDialog({
  kind,
  targetId,
  name,
  onClose,
}: {
  kind: Kind;
  targetId: string;
  name: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const queries = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [days, setDays] = useState<number>(30);
  const [level, setLevel] = useState<Access>('VIEW');

  const links = useQuery({
    queryKey: ['share-links', kind, targetId],
    queryFn: () => api.get<ShareLink[]>(`/api/share-links?kind=${kind}&targetId=${targetId}`),
  });

  /** The one that is actually working, if there is one. */
  const live = (links.data ?? []).find((link) => !link.revokedAt);

  /** The date a given number of days from now, or null for "never". */
  const endingIn = (count: number) =>
    count === 0 ? null : new Date(Date.now() + count * 24 * 60 * 60 * 1000).toISOString();

  /**
   * Mint one.
   *
   * Everything the link says is passed explicitly rather than read off the
   * state, because this is called twice over: once by the Create button, and
   * once when a setting changes on a link that already exists — and that
   * second call must carry the *old* link's terms forward, not whatever the
   * pickers happen to be showing.
   */
  const create = useMutation({
    mutationFn: (input: { allowDownload: boolean; access?: Access; expiresAt?: string | null }) =>
      api.post<ShareLink>('/api/share-links', {
        kind,
        targetId,
        allowDownload: input.allowDownload,
        expiresAt: input.expiresAt === undefined ? endingIn(days) : input.expiresAt,
        ...(kind === 'FOLDER' ? { access: input.access ?? level } : {}),
      }),
    onSuccess: () => void queries.invalidateQueries({ queryKey: ['share-links', kind, targetId] }),
    onError: (error: Error) => toast.error(error.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api/share-links/${id}`),
    onSuccess: () => {
      setCopied(false);
      void queries.invalidateQueries({ queryKey: ['share-links', kind, targetId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const url = live ? `${window.location.origin}/s/${live.token}` : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
    } catch {
      // Clipboard access can be refused — by the browser, by the page not
      // being focused. The field beside the button is selectable, so say so
      // rather than pretending it worked.
      toast.error('Could not copy', 'Select the link in the box and copy it by hand.');
    }
  };

  /**
   * Change what a live link allows.
   *
   * Every one of these turns the old link off and mints a new one, because the
   * link already in somebody's inbox was handed over on the old terms. Said
   * once at the foot of the dialog rather than three times beside the
   * controls.
   */
  const remint = (changes: { access?: Access; allowDownload?: boolean }) => {
    if (!live) return;
    revoke.mutate(live.id, {
      onSuccess: () =>
        create.mutate({
          allowDownload: changes.allowDownload ?? live.allowDownload,
          access: changes.access ?? live.access,
          expiresAt: live.expiresAt,
        }),
    });
  };

  const busy = create.isPending || revoke.isPending;

  return (
    /*
     * The dialog service hands custom content the whole panel, edge to edge,
     * which is right for a form that draws its own header and wrong here:
     * without an inset the rows sat flush against the panel's rounded border.
     */
    <div className="w-[30rem] max-w-full p-5">
      <header>
        <h2 className="text-h2 text-ink">Share a link</h2>
        <p className="mt-0.5 truncate text-meta text-muted" title={name}>
          {name}
        </p>
      </header>

      {links.isLoading ? (
        <p className="flex items-center gap-2 py-10 text-body-sm text-muted">
          <Spinner /> Loading…
        </p>
      ) : (
        <>
          {/*
            One card: who can open it, on what terms, and the link itself.

            The settings used to be stacked beside the description as they were
            thought of — a picker here, a checkbox there, a sentence under each
            one saying the same thing about re-minting. As a list of labelled
            rows they read as one set of terms, and the sentence is said once.
          */}
          <div className="mt-4 overflow-hidden rounded-lg border border-border-strong">
            <div className="flex items-start gap-3 bg-[var(--surface-tint)] p-3.5">
              <span
                className={cx(
                  'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  live ? 'bg-[var(--blue-100)] text-[var(--blue-800)]' : 'bg-sunken text-muted',
                )}
                aria-hidden
              >
                {live ? <GlobeGlyph /> : <LockGlyph />}
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-body-sm font-medium text-ink">
                  {live ? 'Anyone with the link' : 'Restricted'}
                </p>
                <p className="mt-0.5 text-micro leading-relaxed text-muted">
                  {live
                    ? 'Anybody on the internet who has this link can open it, without an account.'
                    : 'Only people signed in to this system, with access to it, can open it.'}
                </p>
              </div>

              {live ? (
                <Button onClick={() => revoke.mutate(live.id)} disabled={busy} className="shrink-0">
                  {revoke.isPending ? 'Turning off…' : 'Turn off'}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => create.mutate({ allowDownload: true })}
                  disabled={busy}
                  className="shrink-0"
                >
                  {create.isPending ? 'Creating…' : 'Create link'}
                </Button>
              )}
            </div>

            {live ? (
              <div className="flex items-center gap-2 border-t border-border p-3">
                <input
                  readOnly
                  value={url}
                  onFocus={(event) => event.currentTarget.select()}
                  aria-label="The link"
                  title={url}
                  className="h-control min-w-0 flex-1 rounded-md border border-border-input bg-sunken px-2.5 font-mono text-meta text-ink"
                />
                <Button variant="primary" icon={<Icons.Copy />} onClick={() => void copy()}>
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              </div>
            ) : null}

            {/* The terms. Label on the left, control on the right, one row
                each — the same shape whether the link exists yet or not, so
                creating one does not rearrange the dialog under the pointer. */}
            <dl className="divide-y divide-border border-t border-border text-body-sm">
              {kind === 'FOLDER' ? (
                <Row label="They can">
                  <select
                    value={live ? live.access : level}
                    disabled={busy}
                    onChange={(event) => {
                      const next = event.target.value as Access;
                      setLevel(next);
                      if (live) remint({ access: next });
                    }}
                    aria-label="What the link lets them do"
                    className="h-control w-full rounded-md border border-border-input bg-surface px-2 text-body-sm text-ink"
                  >
                    {LEVELS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Row>
              ) : null}

              <Row label={live ? 'Stops working' : 'Lasts'}>
                {live ? (
                  <p className="text-body-sm text-ink">
                    {live.expiresAt ? formatDate(live.expiresAt) : 'Never'}
                  </p>
                ) : (
                  <select
                    value={days}
                    onChange={(event) => setDays(Number(event.target.value))}
                    aria-label="How long the link lasts"
                    className="h-control w-full rounded-md border border-border-input bg-surface px-2 text-body-sm text-ink"
                  >
                    {DURATIONS.map((option) => (
                      <option key={option.days} value={option.days}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </Row>

              {live ? (
                <Row label="Copies">
                  <label className="flex items-center gap-2 text-body-sm text-body">
                    <input
                      type="checkbox"
                      checked={live.allowDownload}
                      disabled={busy}
                      onChange={() => remint({ allowDownload: !live.allowDownload })}
                      className="h-4 w-4"
                    />
                    They may download a copy
                  </label>
                </Row>
              ) : null}
            </dl>
          </div>

          {/*
            Said once, where the decision is, and only when it applies.

            A link that can remove things is the one setting whose consequence
            revoking the link later cannot undo — whatever was binned is
            binned. The trash makes that recoverable, which is why this says
            where it goes.
          */}
          {live && kind === 'FOLDER' && live.access !== 'VIEW' ? (
            <div className="mt-3">
              <Alert tone="warning">
                {live.access === 'DELETE'
                  ? 'Anyone with this link can move things in this folder to your trash. Nothing is destroyed — you can put it back — but you will not know who did it, only which link they used.'
                  : 'Anyone with this link can upload into this folder and rename what is in it. Every change is recorded against the link.'}
              </Alert>
            </div>
          ) : null}

          {live ? (
            <p className="mt-3 text-micro leading-relaxed text-muted">
              Changing any of these makes a new link and stops the old one.
            </p>
          ) : null}

          <p className="mt-3 border-t border-border pt-3 text-micro text-subtle">
            {live ? (
              <>
                Created {formatRelative(live.createdAt)}
                {live.createdBy ? ` by ${live.createdBy.name}` : ''} ·{' '}
                {live.views === 0
                  ? 'not opened yet'
                  : `opened ${live.views} time${live.views === 1 ? '' : 's'}${
                      live.lastViewedAt ? `, last ${formatRelative(live.lastViewedAt)}` : ''
                    }`}
              </>
            ) : (
              'Links you turn off are kept in the audit log, with who made them and how often they were opened.'
            )}
          </p>
        </>
      )}

      <div className="mt-4 flex justify-end">
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}

/** One term of the link: what it is called, and the control that sets it. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-3.5 py-2.5">
      <dt className="w-28 shrink-0 text-meta text-muted">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}

function GlobeGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <circle cx="8" cy="8" r="6" />
      <path d="M2.5 8h11M8 2a9 9 0 0 1 0 12A9 9 0 0 1 8 2z" />
    </svg>
  );
}

function LockGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </svg>
  );
}
